import { Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import type {
  QuotationAccount,
  QuotationAccountChange,
  QuotationRequestItem,
} from '@/features/operations/types';
import { StatusChip } from '@/components/StatusChip';

const wrap = { overflowWrap: 'anywhere' } as const;

/// Only a counter quotation whose account is still pending can move (#108, T-4). The backend
/// decides it again on saving; this only decides whether to offer it.
function canCorrectQuotationAccount(
  quotation: Pick<QuotationRequestItem, 'origin' | 'account'>,
): boolean {
  return quotation.origin === 'INTERNAL_PANEL' && quotation.account?.status === 'PENDING';
}

/// Why the account can or cannot be corrected, said next to it, so a missing button explains
/// itself.
function accountHint(quotation: QuotationRequestItem, account: QuotationAccount): string {
  if (quotation.origin !== 'INTERNAL_PANEL') {
    return 'Se cotizó en la web con la sesión de esta cuenta.';
  }

  if (account.status === 'PENDING') {
    return 'Todavía no la activó su dueño. Si el mail se cargó mal en el mostrador, pasá la cotización a la cuenta correcta.';
  }

  return 'La cuenta ya está activa: la cotización es de esa persona y no se puede pasar a otra.';
}

function AccountChangeHistory({ entries }: { entries: QuotationAccountChange[] }) {
  if (!entries.length) {
    return null;
  }

  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        CORRECCIONES DE LA CUENTA
      </Typography>
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
        {entries.map((entry) => (
          <Stack component="li" key={entry.id} spacing={0.25}>
            <Typography variant="body2" sx={wrap}>
              {entry.fromAccount.fullName} ({entry.fromAccount.email}) → {entry.toAccount.fullName}{' '}
              ({entry.toAccount.email})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatHistoryTimestamp(entry.createdAt)} ·{' '}
              {entry.changedBy ? entry.changedBy.fullName : 'PhoneTec'}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

/**
 * The account a quotation belongs to as it reads today (#108), always visible and apart from
 * the contact the request froze (BR-016): correcting one never changes the other.
 */
export function QuotationAccountSection({
  quotation,
  onCorrect,
}: {
  quotation: QuotationRequestItem;
  onCorrect: () => void;
}) {
  const account = quotation.account;

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography variant="overline" color="text.secondary">
          CUENTA
        </Typography>
        {account ? (
          <Stack spacing={0.5}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 0.5, sm: 1 }}
              alignItems={{ sm: 'center' }}
            >
              <Typography variant="body1" fontWeight={600} sx={wrap}>
                {account.fullName}
              </Typography>
              <Box>
                <StatusChip domain="customerAccount" status={account.status} />
              </Box>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={wrap}>
              {account.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {accountHint(quotation, account)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No pudimos leer la cuenta de esta cotización.
          </Typography>
        )}
      </Box>
      {account ? (
        // Buttons rather than the name as a link: an obvious target, like the quotations of the
        // customer sheet. The button's label also names whose sheet it opens.
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            component={RouterLink}
            to={`/admin/customers/${account.id}`}
            variant="outlined"
            aria-label={`Ver cliente ${account.fullName}`}
          >
            Ver cliente
          </Button>
          {canCorrectQuotationAccount(quotation) ? (
            <Button variant="outlined" onClick={onCorrect}>
              Corregir cuenta
            </Button>
          ) : null}
        </Stack>
      ) : null}
      <AccountChangeHistory entries={quotation.accountChanges ?? []} />
    </Stack>
  );
}
